import { useMemo, useState, type ReactNode } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { addDays } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { useAreas } from '../../data/geo.ts'
import { frameFor, type LookFrame } from '../../data/lookGeometry.ts'
import { buildPlan, say, typeLabel, useTemplateVersions, weekOf, type PlanJob, type TaskTypeRow } from '../../data/plan.ts'
import { dayOfMonth, weekdayShort } from '../../data/planUi.ts'
import { buildHistory, trackStack, type TrackStack } from '../../data/settingsHistory.ts'
import { applyParams, proposeSeries, ruleSettings, staleParams, type SettingsWarning } from '../../data/settingsRule.ts'
import { Banner, BackLink, errorText, NotAllowed, PendingBanner } from '../../ui/components.tsx'
import { Picto } from '../../ui/pictos.tsx'
import { LookPicture } from '../../ui/looks/LookPicture.tsx'
import { useSettingsWarnings } from '../../ui/SettingsWarning.tsx'
import { limitOf } from './SettingsPanel.tsx'
import { dayLabel, reasonText, readText, plannedUntil, warningText, type T } from './settingsText.ts'
import { useSettingsData, useToday, type SettingsData } from './useSettingsData.ts'

/** M25-A (docs/M25.md D, "The tracking page"; ADR-131): Planning › Réglages faits et prévus, `/plan/settings?d=` — the week holding that day (Monday first; today's
 *  week by default), in the ADDRESS like `/plan?v=week`, `/plan/print` and `/plan/pins`. One card per series (the jobs of one template, else of one task type) whose
 *  type has pictures: seven columns of picture stacks (28-px pictures, not controls; the day's initial in 13 px) with three marks that never look alike — done and
 *  confirmed (solid), done and not confirmed (solid with a hollow dot, shown as its planned values), planned (in outline) — and a filled dot where the confirmation
 *  differs from the plan; each stack has its accessible text and a legend sits above the first card. Under the strip: the series' « Proposer la semaine » and each
 *  warning (a planned direction or pattern that repeats the previous cut) as a 56-px row to its job. No name of a person anywhere on this page.
 *  The page shows other people's finished work, which only a device holding `job.plan` AND `worklog.read` has (rule 2: a crew phone holds its own finishes alone,
 *  the workstation none): it is the direction's, never a workstation's. */
export function SettingsTrackPage() {
  const { t } = useTranslation()
  const { can, kiosk } = useClub()
  const today = useToday()
  const search = useSearch({ strict: false }) as { d?: string }
  if (kiosk || !can('job.plan')) return <NotAllowed permission="job.plan" />
  if (!can('worklog.read')) return <NotAllowed permission="worklog.read" />
  const day = search.d && /^\d{4}-\d{2}-\d{2}$/.test(search.d) ? search.d : today
  return (
    <section className="flex flex-col gap-4" data-testid="settings-track" data-date={day}>
      <BackLink to="/plan" search={{ d: day }}>← {t('plan.back')}</BackLink>
      <h1 className="text-2xl font-bold">{t('plan.settingsTrack.title')}</h1>
      <Track day={day} today={today} />
    </section>
  )
}

interface Series { key: string; title: string; tt: TaskTypeRow; jobs: PlanJob[] }

function Track({ day, today }: { day: string; today: string }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const data = useSettingsData()
  const tvs = useTemplateVersions()
  const areas = useAreas()
  const days = useMemo(() => weekOf(day), [day])
  const plan = useMemo(() => (data ? buildPlan(data.jobs, data.jobAreas, [], data.taskTypes, lang) : null), [data, lang])
  const frameOf = useMemo(() => { const byId = new Map((areas ?? []).map((a) => [a.id, a.area_type_code])); return (ids: readonly string[]): LookFrame => frameFor(ids.map((id) => byId.get(id) ?? '')) }, [areas])
  const series = useMemo((): Series[] => {
    if (!data || !plan) return []
    const week = new Set(days)
    const groups = new Map<string, Series>()
    for (const j of plan.jobs) {
      if (!week.has(j.scheduled_local_date) || j.state === 'cancelled') continue
      const tt = data.taskTypes.find((x) => x.code === j.task_type_code)
      if (!tt || ruleSettings(tt, lang).length === 0) continue
      const tv = (tvs ?? []).find((v) => v.id === j.template_version_id)
      const key = tv ? `tpl:${tv.template_id}` : `type:${j.task_type_code}`
      const g = groups.get(key)
      if (g) g.jobs.push(j)
      else groups.set(key, { key, title: (tv && say(tv.title, lang)) || typeLabel(tt, tt.code, lang) || t('plan.settingsTrack.seriesFallback'), tt, jobs: [j] })
    }
    return [...groups.values()].sort((a, b) => a.title.localeCompare(b.title, lang) || a.key.localeCompare(b.key))
  }, [data, plan, days, tvs, lang, t])
  const planned = useMemo(() => series.flatMap((s) => s.jobs).filter((j) => j.state === 'planned'), [series])
  const warnings = useSettingsWarnings(planned)
  if (!data || !plan) return <p className="opacity-70">{t('common.loading')}</p>

  return (
    <>
      <p className="meta">{t('plan.settingsTrack.intro')}</p>
      <div className="flex flex-col gap-2">
        <p className="text-lg font-bold first-letter:uppercase" data-testid="track-week" data-from={days[0]}>{t('plan.settingsTrack.weekOf', { day: dayLabel(days[0]!, lang) })}</p>
        <div className="grid grid-cols-2 gap-2">
          <Link to="/plan/settings" search={{ d: addDays(day, -7) } as never} className="btn" data-testid="track-prev">{t('plan.settingsTrack.prev')}</Link>
          <Link to="/plan/settings" search={{ d: addDays(day, 7) } as never} className="btn" data-testid="track-next">{t('plan.settingsTrack.next')}</Link>
        </div>
      </div>
      {series.length === 0 ? <p data-testid="track-empty">{t('plan.settingsTrack.empty')}</p> : (
        <>
          <Legend />
          {series.map((s) => <SeriesCard key={s.key} series={s} days={days} data={data} today={today} frameOf={frameOf} warnings={warnings} />)}
        </>
      )}
    </>
  )
}

/** the three marks and the dot, each as the stack draws it */
const MARK_CLASS: Record<TrackStack['mark'], string> = {
  confirmed: 'border-2 border-brand bg-fairway-light text-brand-dark dark:border-emerald-600 dark:bg-night-line dark:text-fairway',
  unconfirmed: 'border-2 border-fairway bg-white text-slate-800 dark:border-night-line dark:bg-night-card dark:text-slate-100',
  planned: 'border-2 border-dashed border-slate-400 bg-transparent text-slate-700 dark:border-slate-500 dark:text-slate-200',
}
const HollowDot = () => <span data-mark-dot="hollow" aria-hidden="true" className="absolute right-0.5 top-0.5 size-2.5 rounded-full border-2 border-current bg-transparent" />
const FilledDot = () => <span data-mark-dot="filled" aria-hidden="true" className="absolute left-0.5 top-0.5 size-3 rounded-full border border-sand-ink bg-sand-ink dark:border-sand dark:bg-sand" />

function Legend() {
  const { t } = useTranslation()
  const sample = (mark: TrackStack['mark'], extra?: ReactNode) => <span aria-hidden="true" className={`relative inline-block size-8 shrink-0 rounded-md ${MARK_CLASS[mark]}`}>{extra}</span>
  return (
    <div className="card flex flex-col gap-2" data-testid="track-legend" role="group" aria-label={t('plan.settingsTrack.legendTitle')}>
      <p className="text-sm font-semibold">{t('plan.settingsTrack.legendTitle')}</p>
      <ul className="flex flex-col gap-1.5 text-sm">
        <li className="flex items-center gap-2">{sample('confirmed')}{t('plan.settingsTrack.legend.confirmed')}</li>
        <li className="flex items-center gap-2">{sample('unconfirmed', <HollowDot />)}{t('plan.settingsTrack.legend.unconfirmed')}</li>
        <li className="flex items-center gap-2">{sample('planned')}{t('plan.settingsTrack.legend.planned')}</li>
        <li className="flex items-center gap-2">{sample('confirmed', <FilledDot />)}{t('plan.settingsTrack.legend.differs')}</li>
      </ul>
    </div>
  )
}

function SeriesCard({ series, days, data, today, frameOf, warnings }: {
  series: Series; days: string[]; data: SettingsData; today: string; frameOf: (ids: readonly string[]) => LookFrame; warnings: ReadonlyMap<string, SettingsWarning[]>
}) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const stacksByDay = useMemo(() => {
    const m = new Map<string, Array<{ job: PlanJob; stack: TrackStack }>>()
    for (const j of series.jobs) {
      const stack = trackStack(j, series.tt, data.completions, data.confirmations)
      if (stack) m.set(j.scheduled_local_date, [...(m.get(j.scheduled_local_date) ?? []), { job: j, stack }])
    }
    return m
  }, [series, data])
  const rows = series.jobs.filter((j) => warnings.has(j.id)).sort((a, b) => a.scheduled_local_date.localeCompare(b.scheduled_local_date) || a.id.localeCompare(b.id))
  const upcoming = series.jobs.filter((j) => j.state === 'planned' && j.scheduled_local_date >= today)
  return (
    <section className="card flex flex-col gap-3" data-testid="track-series" data-series={series.key}>
      <h2 className="text-lg font-bold">{series.title}</h2>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => (
          <div key={d} className="flex min-w-0 flex-col items-center gap-1">
            <span className="text-[13px] font-semibold first-letter:uppercase" data-testid="track-day-head">{weekdayShort(d, lang).slice(0, 1)} {dayOfMonth(d)}</span>
            <ul className="flex w-full flex-col items-center gap-1">
              {(stacksByDay.get(d) ?? []).map(({ job, stack }) => {
                const parts = [t(`plan.settingsTrack.mark.${stack.mark}`), ...(stack.differs ? [t('plan.settingsTrack.mark.differs')] : [])]
                const frame = frameOf(job.areaIds)
                return (
                  <li key={job.id} role="group" aria-label={`${parts.join(', ')} : ${stack.items.map((i) => i.word).join(', ')}`} data-testid="track-stack" data-job={job.id} data-mark={stack.mark} data-differs={stack.differs}
                    className={`relative flex min-h-9 w-full flex-col items-center justify-center gap-0.5 rounded-md p-1 ${MARK_CLASS[stack.mark]}`}>
                    {stack.items.map((i) => (i.code ? <LookPicture key={i.key} code={i.code} frame={frame} className="size-7 shrink-0" /> : null))}
                    {stack.mark === 'unconfirmed' && <HollowDot />}
                    {stack.differs && <FilledDot />}
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
      {rows.length > 0 && (
        <ul className="flex flex-col gap-1.5" data-testid="track-warnings">
          {rows.map((j) => {
            const w = warnings.get(j.id)![0]!
            return (
              <li key={j.id}>
                <Link to="/plan/job/$jobId" params={{ jobId: j.id }} className="row-link" data-testid="track-warning" data-job={j.id}>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="text-base font-semibold leading-snug">{warningText(t as T, w, lang)}</span>
                    <span className="meta">{t('plan.settingsTrack.warnHint')}</span>
                  </span>
                  <Picto name="chevron" className="size-5 shrink-0 opacity-40" />
                </Link>
              </li>
            )
          })}
        </ul>
      )}
      {upcoming.length > 0 && <WeekProposal series={series} jobs={upcoming} data={data} today={today} frame={frameOf(upcoming[0]!.areaIds)} />}
    </section>
  )
}

/** « Proposer la semaine »: the series' planned jobs of the week in day order, each proposed by the written rule with the proposals before it read in place of
 *  their planned values; the two numbers typed once for the list; one tick per job; one « Appliquer » sends one `job.update@1` per ticked job in day order, each
 *  `sendAndWait` with its own `expected_revision` (M18's precedent: a proposal applied line by line). A refusal — a `REVISION_CONFLICT` included — stops the rest and
 *  says which were applied (the proposals recompute from what the device then holds). One `PendingBanner` for the list. */
function WeekProposal({ series, jobs, data, today, frame }: { series: Series; jobs: PlanJob[]; data: SettingsData; today: string; frame: LookFrame }) {
  const { t, i18n } = useTranslation()
  const tf = t as T
  const lang = i18n.language
  const { site, sendAndWait } = useClub()
  const [open, setOpen] = useState(false)
  const [lapsText, setLapsText] = useState('')
  const [groomersText, setGroomersText] = useState('')
  const [off, setOff] = useState<ReadonlySet<string>>(new Set())
  const [clear, setClear] = useState(true)
  const [busy, setBusy] = useState(false)
  const [pending, setPending] = useState(false)
  const [result, setResult] = useState<{ applied: number; stopped: string | null } | null>(null)
  const settings = useMemo(() => ruleSettings(series.tt, lang), [series.tt, lang])
  const ordered = useMemo(() => [...jobs].sort((a, b) => a.scheduled_local_date.localeCompare(b.scheduled_local_date) || a.id.localeCompare(b.id)), [jobs])
  const history = useMemo(() => {
    if (!open) return null
    const last = ordered[ordered.length - 1]!.scheduled_local_date
    return buildHistory({
      tt: series.tt, jobs: data.jobs, jobAreas: data.jobAreas, completions: data.completions, coverage: data.coverage, confirmations: data.confirmations,
      areaIds: [...new Set(ordered.flatMap((j) => j.areaIds))], today, until: addDays(last, 1), tz: site.timezone, readsWork: true, excludePlannedJobs: new Set(ordered.map((j) => j.id)),
    })
  }, [open, ordered, series.tt, data, today, site.timezone])
  const proposals = useMemo(() => (history ? proposeSeries({ settings, lines: history.lines }, ordered.map((j) => ({ id: j.id, day: j.scheduled_local_date, areaIds: j.areaIds, params: j.params })), { lapsPerWeek: limitOf(lapsText), groomersPerWeek: limitOf(groomersText) }) : []),
    [history, settings, ordered, lapsText, groomersText])
  if (!open) return <button type="button" className="btn self-start" onClick={() => setOpen(true)} data-testid="track-propose">{t('plan.settingsTrack.proposeWeek')}</button>

  const rowOf = (id: string) => proposals.find((p) => p.job.id === id)
  const valuesOf = (id: string): Record<string, string> => Object.fromEntries((rowOf(id)?.proposal.settings ?? []).filter((s) => s.picks.length > 0).map((s) => [s.key, s.picks[0]!.word]))
  const ticked = ordered.filter((j) => !off.has(j.id) && Object.keys(valuesOf(j.id)).length > 0)
  const staleOf = (j: PlanJob) => staleParams(j.params, series.tt, lang).filter((s) => !Object.hasOwn(valuesOf(j.id), s.key))
  const staleJobs = ticked.filter((j) => staleOf(j).length > 0)
  const hold = staleJobs.length > 0 && !clear
  const hasLap = settings.some((s) => s.family === 'lap'), hasTool = settings.some((s) => s.family === 'tool')

  const apply = async () => {
    setBusy(true); setResult(null); setPending(false)
    let applied = 0, stopped: string | null = null
    try {
      for (const j of ticked) {
        const params = applyParams(j.params, valuesOf(j.id), clear ? staleParams(j.params, series.tt, lang).map((s) => s.key) : [])
        try {
          const v = await sendAndWait('job.update@1', { job_id: j.id, expected_revision: j.revision, set: { params } })
          if (v.status === 'applied' || v.status === 'applied_with_exception') { applied++; continue }
          if (v.status === 'queued') { setPending(true); break }
          stopped = errorText(t, v.code); break
        } catch (e) { stopped = e instanceof Error && e.message ? e.message : t('errors.unknown'); break }
      }
    } finally { setBusy(false) }
    setResult({ applied, stopped })
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-slate-200 p-3 dark:border-night-line" data-testid="track-week-panel">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-lg font-bold">{t('plan.settingsTrack.weekTitle')}</h3>
        <button type="button" className="btn !px-4" onClick={() => setOpen(false)} disabled={busy} data-testid="track-week-close">{t('plan.settingsTrack.close')}</button>
      </div>
      <p className="text-sm" data-testid="settings-notai">{t('plan.settingsRule.notAI')}</p>
      {history && <p className="meta" data-testid="settings-read">{readText(tf, history, plannedUntil(addDays(ordered[ordered.length - 1]!.scheduled_local_date, 1)))}</p>}
      {(hasLap || hasTool) && (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-semibold">{t('plan.settingsPanel.numbersTitle')}</legend>
          {hasLap && (
            <div className="flex flex-col gap-1">
              <label htmlFor="week-laps" className="text-sm font-semibold">{t('plan.settingsPanel.lapsLabel')}</label>
              <input id="week-laps" className="input sm:max-w-xs" inputMode="numeric" value={lapsText} onChange={(e) => setLapsText(e.target.value)} data-testid="settings-laps" />
              <p className="meta">{t('plan.settingsPanel.lapsHelp')}</p>
            </div>
          )}
          {hasTool && (
            <div className="flex flex-col gap-1">
              <label htmlFor="week-groomers" className="text-sm font-semibold">{t('plan.settingsPanel.groomersLabel')}</label>
              <input id="week-groomers" className="input sm:max-w-xs" inputMode="numeric" value={groomersText} onChange={(e) => setGroomersText(e.target.value)} data-testid="settings-groomers" />
              <p className="meta">{t('plan.settingsPanel.groomersHelp')}</p>
            </div>
          )}
        </fieldset>
      )}
      <ul className="flex flex-col gap-1.5">
        {proposals.map(({ job, proposal }) => {
          const picks = proposal.settings.filter((s) => s.picks.length > 0)
          return (
            <li key={job.id} className="rounded-xl border border-slate-200 p-2 dark:border-night-line" data-testid="track-week-job" data-job={job.id}>
              <label className="flex min-h-14 items-center gap-3">
                <input type="checkbox" className="size-6 shrink-0 accent-brand" checked={!off.has(job.id) && picks.length > 0} disabled={picks.length === 0 || busy}
                  onChange={() => setOff((cur) => { const next = new Set(cur); if (next.has(job.id)) next.delete(job.id); else next.add(job.id); return next })} data-testid="track-week-tick" />
                <span className="font-semibold first-letter:uppercase">{dayLabel(job.day, lang)}</span>
                {picks.map((s) => (
                  <span key={s.key} className="inline-flex items-center gap-1">
                    <LookPicture code={s.picks[0]!.code} frame={frame} className="size-8 shrink-0 text-brand dark:text-fairway" />
                    <span>{s.picks[0]!.word}</span>
                  </span>
                ))}
              </label>
              <details>
                <summary className="min-h-14 cursor-pointer content-center text-sm font-semibold">{t('plan.settingsTrack.reasons')}</summary>
                <ul className="meta flex flex-col gap-0.5">{picks.map((s) => <li key={s.key}><span className="font-semibold">{s.label}</span> : {reasonText(tf, s.picks[0]!.reason)}</li>)}</ul>
              </details>
            </li>
          )
        })}
      </ul>
      {staleJobs.length > 0 && (
        <Banner tone="warn" testId="track-week-stale">
          <ul className="list-disc pl-5">
            {staleJobs.map((j) => <li key={j.id}>{dayLabel(j.scheduled_local_date, lang)} : {t('plan.settingsTrack.staleJob', { list: staleOf(j).map((s) => (s.gone ? s.label : `${s.label} « ${String(s.word)} »`)).join(', ') })}</li>)}
          </ul>
          <label className="mt-2 flex min-h-14 items-center gap-3 font-semibold">
            <input type="checkbox" className="size-6 shrink-0 accent-brand" checked={clear} onChange={(e) => setClear(e.target.checked)} data-testid="track-week-stale-clear" />
            {t('plan.settingsPanel.staleClear')}
          </label>
          {hold && <p className="mt-1">{t('plan.settingsPanel.staleHold')}</p>}
        </Banner>
      )}
      {ticked.length === 0 && <p className="meta">{t('plan.settingsTrack.nothingTicked')}</p>}
      <p className="text-sm" data-testid="track-week-sentence">{t('plan.settingsTrack.applySentence')}</p>
      <Link to="/plan/setup" className="btn self-start" data-testid="track-rule-link">{t('plan.settingsTrack.ruleLink')}</Link>
      {pending && <PendingBanner />}
      {result && (
        <div aria-live="polite" data-testid="track-week-result" className="text-sm font-semibold">
          {result.stopped ? t('plan.settingsTrack.stopped', { applied: result.applied, reason: result.stopped }) : result.applied > 0 ? t('plan.settingsTrack.applied', { count: result.applied }) : ''}
        </div>
      )}
      <button type="button" className="btn btn-primary" disabled={busy || pending || ticked.length === 0 || hold} onClick={() => void apply()} data-testid="track-week-apply">
        {busy ? t('plan.settingsTrack.applying') : t('plan.settingsTrack.apply')}
      </button>
    </div>
  )
}
